
import { Vue, Component, Prop } from "vue-property-decorator";
import {Getter} from "vuex-class";
import { Datetime } from "vue-datetime";
import "vue-datetime/dist/vue-datetime.css";
import MylobyAutocomplete from "@/components/generic/autocomplete.vue";
import SimpleDialog from "@/components/SimpleDialog.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiDoorOpen, mdiKey, mdiKeyChange } from "@mdi/js";

@Component({
  components: { Datetime, MylobyAutocomplete, SimpleDialog, SvgIcon },
})
export default class MissionOrderDetailsModal extends Vue {

    @Prop({default: null})
    public missionOrderId: number;

    @Prop({default: null})
    public buildingId: number;

    @Getter private isMobile: boolean;
    @Getter private envCompanyId: number|null;

    private showDialog: boolean = true;
    private loading: boolean = false;
    private wording: any = {
      ok: this.$i18n.t("actions.next"),
      cancel: this.$i18n.t("actions.cancel"),
    };
    private missionOrder: any = null;
    private displayCreateKeyGroup: boolean = false;
    private newKeyGroup: any = null;
    private buildingGroups: any = [];
    private buildingKeys: any = [];
    private buildingNonTransferables: any = [];
    private step: "CREATE"|"EDIT"|"CREATE_CONFIRMATION"|"DELETE_CONFIRMATION" = "CREATE";

    get isNewMissionOrder(): boolean {
      return !this.missionOrderId;
    }

    get isFormValid(): boolean {
      return !!this.missionOrder?.type
        && this.missionOrder?.serviceCompanyId
        && this.missionOrder?.countAccessLeft
        && this.missionOrder?.startDate
        && (this.missionOrder?.keyGroupId || (
          this.newKeyGroup?.name && (
            this.newKeyGroup?.keyIds?.length || this.newKeyGroup?.nonTransferableIds?.length
          )
        ));
    }

    get dialogActions(): string[] {
      const actions: string[] = ["cancel", "confirm"];
      // If it's an existing mission order, user can delete it
      if (this.step === "EDIT") {
        actions.unshift("delete");
      }
      return actions;
    }

    private async mounted() {
      if (this.isNewMissionOrder) {
        this.step = "CREATE";
        this.initMissionOrder();
      } else {
        this.step = "EDIT";
        await this.getMissionOrder();
      }
      if (this.buildingId) {
        this.getLinkedObjectsGroupsByBuildingId();
      }
    }

    private initMissionOrder(): void {
      this.missionOrder = {
        serviceCompanyId: null,
        buildingId: this.buildingId,
        type: null,
        countAccessLeft: -1,
        startDate: new Date().toISOString(),
        endDate: null,
        keyGroupId: null,
      };
      this.resetKeyGroupFields();
    }

    private changeMissionOrderBuilding(): void {
      this.missionOrder.keyGroupId = null;
      this.cancelKeyGroupCreation();
      if (this.missionOrder.buildingId) {
        this.getLinkedObjectsGroupsByBuildingId();
      }
    }

    private displayCreateKeyGroupFields(): void {
      this.resetKeyGroupFields();
      this.getLinkedKeysByBuildingId();
      this.getLinkedNonTransferablesByBuildingId();
      this.displayCreateKeyGroup = true;
    }

    private cancelKeyGroupCreation(): void {
      this.displayCreateKeyGroup = false;
      this.resetKeyGroupFields();
    }

    private resetKeyGroupFields(): void {
      this.newKeyGroup = {
        name: null,
        keyIds: [],
        nonTransferableIds: [],
      };
      this.buildingKeys = [];
      this.buildingNonTransferables = [];
    }

    private async cancel() {
      if (this.step === "CREATE_CONFIRMATION") {
        this.step = "CREATE";
      } else {
        this.$emit("close");
        this.initMissionOrder();
      }
    }

    private async confirm(): Promise<void> {
      switch (this.step) {
        case "CREATE":
          if (this.displayCreateKeyGroup) {
            // No need to check for duplicates since it's a new keyGroup
            return this.createMissionOrder();
          } else {
            return this.checkExistingMissionOrder();
          }
        case "CREATE_CONFIRMATION":
          return this.createMissionOrder();
        case "EDIT":
          return this.saveMissionOrder();
        case "DELETE_CONFIRMATION":
          return this.deleteMissionOrder();
        default:
          break;
      }
    }

    private async getMissionOrder(): Promise<any> {
        this.loading = true;
        await this.$store.dispatch("missionOrders/readById", {
            id: this.missionOrderId,
        }).then((res) => {
            this.missionOrder = res;
            const keyAccess = this.missionOrder.KeyAccess;
            this.missionOrder.countAccessLeft = keyAccess.countAccessLeft;
            this.missionOrder.startDate = keyAccess.startDate;
            this.missionOrder.endDate = keyAccess.endDate;
            this.missionOrder.keyGroupId = keyAccess.keyGroupId;
            this.getLinkedObjectsGroupsByBuildingId();
        }).catch((error) => {
            this.$store.commit("alerts/displayHttpError", { error });
        }).finally(() => {
            this.loading = false;
        });
    }

    private async checkExistingMissionOrder(): Promise<void> {
      this.loading = true;
      await this.$store.dispatch("missionOrders/read", {
        // Filters
        envCompanyId: this.envCompanyId,
        clientCompanyId: this.envCompanyId,
        serviceCompanyId: this.missionOrder.serviceCompanyId,
        buildingId: this.missionOrder.buildingId,
        keyGroupId: this.missionOrder.keyGroupId,
        type: this.missionOrder.type,
      }).then(async (data) => {
        if (data.count > 0) {
          this.step = "CREATE_CONFIRMATION";
          this.loading = false;
        } else {
          return this.createMissionOrder();
        }
      }).catch(() => {
        this.loading = false;
      });
    }

    private async createMissionOrder(): Promise<void> {
        this.loading = true;
        if (this.displayCreateKeyGroup) {
          await this.createKeyGroup();
        }
        if (this.missionOrder.type === "MAINTENANCE_CONTRACT") {
          this.missionOrder.countAccessLeft = -1;
        }
        await this.$store.dispatch("missionOrders/create", {
            clientCompanyId: this.envCompanyId,
            ...this.missionOrder,
        }).then(() => {
            this.$emit("reset");
            this.$store.commit("alerts/displaySuccess", {
                msg: this.$i18n.t("MissionOrders.created"),
            });
        }).catch((error) => {
            this.$store.commit("alerts/displayHttpError", { error });
        }).finally(() => {
            this.loading = false;
        });
    }

    private async saveMissionOrder(): Promise<void> {
      this.loading = true;
      if (this.missionOrder.type === "MAINTENANCE_CONTRACT") {
        this.missionOrder.countAccessLeft = -1;
      }
      await this.$store.dispatch("missionOrders/update", this.missionOrder).then(() => {
        this.$emit("reset");
        this.$store.commit("alerts/displaySuccess", {
          msg: this.$i18n.t("MissionOrders.updated"),
        });
      }).catch((error) => {
        this.$store.commit("alerts/displayHttpError", { error });
      }).finally(() => {
        this.loading = false;
      });
    }

    private async deleteMissionOrder(): Promise<void> {
      this.loading = true;
      await this.$store.dispatch("missionOrders/delete", {
        id: this.missionOrderId,
      }).then(() => {
        this.$emit("reset");
        this.$store.commit("alerts/displaySuccess", {
          msg: this.$i18n.t("MissionOrders.deleted"),
        });
      }).catch((error) => {
        this.$store.commit("alerts/displayHttpError", { error });
      }).finally(() => {
        this.loading = false;
      });
    }

    private async getLinkedKeysByBuildingId() {
      this.buildingKeys = [];
      await this.$store.dispatch("buildings/getLinkedKeysByBuildingId", {
        buildingId: this.missionOrder.buildingId,
      }).then((data) => {
        this.buildingKeys = data.rows;
      });
    }

    private async getLinkedNonTransferablesByBuildingId() {
      this.buildingNonTransferables = [];
      await this.$store.dispatch("buildings/getNonTransferablesByBuildingId", {
        buildingId: this.missionOrder.buildingId,
      }).then((data) => {
        this.buildingNonTransferables = data.rows;
      });
    }

    private async getLinkedObjectsGroupsByBuildingId() {
      this.buildingGroups = [];
      await this.$store.dispatch("buildings/getObjectsGroupsByBuildingId", {
        buildingId: this.missionOrder.buildingId,
      }).then((data) => {
        this.buildingGroups = data.rows;
      });
    }

    private async createKeyGroup(): Promise<void> {
      this.loading = true;
      await this.$store.dispatch("keyGroups/create", {
        keyGroupData: {
          name: this.newKeyGroup.name,
          companyId: this.envCompanyId,
          buildingId: this.missionOrder.buildingId,
        },
        keyIds: this.newKeyGroup.keyIds,
        nonTransferableIds: this.newKeyGroup.nonTransferableIds,
      }).then((res) => {
        this.missionOrder.keyGroupId = res.id;
      }).catch((error) => {
        this.$store.commit("alerts/displayHttpError", { error });
        throw(error);
      }).finally(() => {
        this.loading = false;
      });
    }

    private getGroupName = (group): string => group.name
      || group.Keys?.[0]?.name
      || group.NonTransferables?.[0]?.name

    private getGroupIcon = (group): string => {
      if (group.name) {
        return mdiKeyChange;
      } else if (group.Keys?.[0]) {
        return mdiKey;
      } else if (group.NonTransferables?.[0]) {
        return mdiDoorOpen;
      }
    }
}
